// One-shot deep-link intents between marketing views ("go to Settings and
// scroll to the domain card", "open the composer on the AI writer").
//
// Module scope on purpose: a sidebar jump can remount MarketingSaaS (and so
// drop its state) before the target view renders, so the intent can't live
// in component state. The target view takes it once, on mount.
let pending = null;
// See suppressScrollReset below for why this outlives `pending` itself.
let suppressResetUntil = 0;
const SUPPRESS_MS = 1500;

export const setNavIntent = (intent) => {
  pending = intent ? { ...intent, at: Date.now() } : null;
  if (pending) suppressResetUntil = Date.now() + SUPPRESS_MS;
};

// Returns the pending intent if it matches `kind` and is fresh, then clears it.
export const takeNavIntent = (kind) => {
  if (!pending || pending.kind !== kind) return null;
  const intent = pending;
  pending = null;
  return Date.now() - intent.at < 15000 ? intent : null;
};

// App.jsx's <ScrollToTop> checks this so it doesn't reset the page to (0,0)
// out from under a deep link that's about to scroll somewhere else on its
// own (e.g. the setup checklist's "Add domain", which crosses tabs via a URL
// change: jumpTab -> onNavigate -> navigate()).
//
// This can't just be "is an intent still pending": setNavIntent runs before
// jumpTab's synchronous setActiveSubTab, so the target view mounts and
// consumes the intent (clearing `pending`) in the FIRST commit, often before
// react-router's own history listener has propagated the new `pathname` to
// <ScrollToTop> at all. That second, router-driven commit lands a few ms
// later, by which point `pending` is already null — so checking `pending`
// alone still lets ScrollToTop's instant reset win the race and snap an
// already-in-progress smooth scrollIntoView back to the top. A short time
// window that outlives the intent's consumption covers that gap.
export const suppressScrollReset = () => Date.now() < suppressResetUntil;

// Scroll a section into view once it exists (views render a tick later).
export const scrollToSection = (id, tries = 10) => {
  if (typeof document === 'undefined') return;
  const el = document.getElementById(id);
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    return;
  }
  if (tries > 0) setTimeout(() => scrollToSection(id, tries - 1), 100);
};
